import React, { useEffect, useRef, useState } from 'react'

import type { AgentChatMessage } from '~/api/agentChat'

export interface MessageGroup {
  agentMessages: AgentChatMessage[]
  agentWrapperRef: React.RefObject<HTMLDivElement>
  userMessage: AgentChatMessage
  userMessageRef: React.RefObject<HTMLDivElement>
}

export interface UseMessageScrollBehaviorReturn {
  containerHeight: number
  containerRef: React.RefObject<HTMLDivElement>
  messageGroups: MessageGroup[]
  messagesEndRef: React.RefObject<HTMLDivElement>
}

/**
 * Hook to manage scroll behavior where user messages stay at the top
 * and agent messages fill the space below with a minimum height wrapper.
 */
export const useMessageScrollBehavior = (
  messages: AgentChatMessage[],
  sessionId: string | null,
): UseMessageScrollBehaviorReturn => {
  const containerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState(0)
  const previousMessagesLengthRef = useRef<number>(0)
  const previousSessionIdRef = useRef<string | null>(null)
  const isInitialMount = useRef(true)
  const refsMapRef = useRef<
    Map<
      string,
      {
        agentRef: React.RefObject<HTMLDivElement>
        userRef: React.RefObject<HTMLDivElement>
      }
    >
  >(new Map())

  // Group messages: each user message followed by its agent responses
  const messageGroups = React.useMemo(() => {
    const groups: MessageGroup[] = []
    let currentGroup: MessageGroup | null = null

    messages.forEach((message) => {
      if (message.sender === 'user') {
        // Get or create refs for this user message
        if (!refsMapRef.current.has(message.id)) {
          refsMapRef.current.set(message.id, {
            userRef: React.createRef<HTMLDivElement>(),
            agentRef: React.createRef<HTMLDivElement>(),
          })
        }
        const refs = refsMapRef.current.get(message.id)!

        // Start a new group
        currentGroup = {
          userMessage: message,
          agentMessages: [],
          userMessageRef: refs.userRef,
          agentWrapperRef: refs.agentRef,
        }
        groups.push(currentGroup)
      } else if (currentGroup) {
        // Add agent message to current group
        currentGroup.agentMessages.push(message)
      }
    })

    return groups
  }, [messages])

  // Measure container height
  useEffect(() => {
    const container = containerRef.current
    if (!container) return () => {}

    const updateHeight = () => {
      setContainerHeight(container.clientHeight)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Reset on session change
  useEffect(() => {
    const previousSessionId = previousSessionIdRef.current
    const isSessionChange = previousSessionId !== sessionId

    if (isSessionChange) {
      previousMessagesLengthRef.current = 0
      isInitialMount.current = true
      refsMapRef.current.clear()
    }

    previousSessionIdRef.current = sessionId
  }, [sessionId])

  // Handle scroll behavior: scroll to bottom on initial mount, scroll to top for new user messages
  useEffect(() => {
    const currentLength = messages.length
    const previousLength = previousMessagesLengthRef.current
    const hasNewMessage = currentLength > previousLength

    // Scroll to bottom on initial mount when messages exist
    if (
      isInitialMount.current &&
      sessionId !== null &&
      messages.length > 0 &&
      messageGroups.length > 0 &&
      containerRef.current
    ) {
      const lastMessage = messages[messages.length - 1]
      // Only scroll for existing sessions (has agent messages), not new empty sessions
      if (lastMessage?.sender === 'agent' || messageGroups.length > 1) {
        // Use double requestAnimationFrame to ensure DOM is ready and refs are attached
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Scroll to the messagesEndRef anchor at the bottom
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'end',
              })
            } else if (containerRef.current) {
              // Fallback: scroll container directly
              containerRef.current.scrollTop = containerRef.current.scrollHeight
            }
          })
        })
      }
      previousMessagesLengthRef.current = currentLength
      isInitialMount.current = false
      return
    }

    // Scroll to top when new user message is added
    if (hasNewMessage) {
      const newlyAddedMessages = messages.slice(previousLength)
      const isNewUserMessage = newlyAddedMessages.some(
        (message) => message.sender === 'user',
      )

      if (isNewUserMessage && containerRef.current) {
        const lastGroup = messageGroups[messageGroups.length - 1]
        if (lastGroup?.userMessageRef.current) {
          requestAnimationFrame(() => {
            lastGroup.userMessageRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          })
        }
      }
    }

    previousMessagesLengthRef.current = currentLength
  }, [messages, messageGroups, sessionId])

  return {
    containerHeight,
    messageGroups,
    containerRef,
    messagesEndRef,
  }
}
